import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import EthiopianFlag from '../components/EthiopianFlag';
import './Landing.css';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [currentStat, setCurrentStat] = useState(0);

  const stats = [
    { number: 'Most', label: 'students do not pass' },
    { number: '500K+', label: 'students fail every year' },
    { number: '15x', label: 'gap between schools' },
  ];

  // Rotate stats for mobile
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <nav className="landing-nav">
          <div className="nav-brand">
            <Logo height={36} />
          </div>
          <div className="nav-links">
            <button className="nav-link" onClick={() => navigate('/about')}>
              About
            </button>
            <button className="nav-link login-link" onClick={() => navigate('/login')}>
              Login
            </button>
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-badge">
            <EthiopianFlag size={14} style={{ marginRight: 4 }} /> Built in Ethiopia, for Ethiopia
          </div>

          <h1 className="hero-title">
            <span className="title-icon">🍋</span>
            <span className="title-text">Lomi</span>
            <span className="title-amharic">ሎሚ</span>
          </h1>

          <p className="hero-tagline">Your AI Tutor for the ESSLCE</p>

          <p className="hero-subtitle">
            Fresh learning, clear understanding
          </p>

          <div className="hero-cta">
            <button
              className="cta-primary"
              onClick={() => navigate('/login')}
            >
              Start Learning Free
            </button>
            <button
              className="cta-secondary"
              onClick={() => navigate('/about')}
            >
              Our Story
            </button>
          </div>

          <div className="hero-stats-mobile">
            <div className="stat-rotating">
              <span className="stat-number">{stats[currentStat].number}</span>
              <span className="stat-label">{stats[currentStat].label}</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="phone-mockup">
            <div className="phone-screen">
              <div className="screen-header">
                <span>🍋</span> Lomi AI
              </div>
              <div className="screen-content">
                <div className="chat-bubble student">
                  Why is photosynthesis important?
                </div>
                <div className="chat-bubble lomi">
                  <span className="lomi-icon">🍋</span>
                  Great question! Photosynthesis is how plants make food using sunlight. Think of it like cooking — but the ingredients are light, water, and CO₂...
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Section */}
      <section className="crisis-section">
        <div className="crisis-content">
          <h2>The Crisis We're Solving</h2>
          <p className="crisis-intro">
            Every year, hundreds of thousands of Ethiopian students fail the national exam.
            Not because they're not smart enough. But because they don't have access to great teaching.
          </p>

          <div className="stats-grid">
            <div className="stat-card urgent">
              <div className="stat-number">Most</div>
              <div className="stat-label">students do not pass</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">5.4%</div>
              <div className="stat-label">Pass rate in government schools</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">86%</div>
              <div className="stat-label">Pass rate in international schools</div>
            </div>
          </div>

          <p className="crisis-callout">
            The difference isn't intelligence. <strong>It's access.</strong>
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="solution-content">
          <h2>A Great Teacher in Your Pocket</h2>
          <p className="solution-intro">
            Lomi is an AI tutor built specifically for Ethiopian students
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>Real ESSLCE Questions</h3>
              <p>Practice with actual past exam questions. Know exactly what to expect.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Personalized Learning</h3>
              <p>Lomi adapts to you. Struggling somewhere? It focuses there.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🍋</div>
              <h3>Ask Lomi AI</h3>
              <p>Stuck on a concept? Ask Lomi. Get clear explanations instantly.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Always Available</h3>
              <p>Study at 6am or midnight. Lomi is always ready to help.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Lomi Section */}
      <section className="why-section">
        <div className="why-content">
          <div className="why-visual">
            <div className="lemon-large">🍋</div>
            <div className="amharic-large">ሎሚ</div>
          </div>
          <div className="why-text">
            <h2>Why "Lomi"?</h2>
            <p>
              <strong>ሎሚ</strong> means "lemon" in Amharic. Just like a fresh lemon
              brings clarity and brightness, Lomi brings fresh understanding to complex topics.
            </p>
            <ul className="why-list">
              <li><span>🌱</span> <strong>Fresh</strong> — New approach to learning</li>
              <li><EthiopianFlag size={14} style={{ marginRight: 6 }} /> <strong>Ethiopian</strong> — Built here, for here</li>
              <li><span>🧠</span> <strong>AI-Powered</strong> — Your personal tutor, 24/7</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="story-content">
          <h2>Why We Built Lomi</h2>
          <blockquote>
            "In the late 90s, I walked through the gates of Medhane Alem School —
            'Medesh' — in Addis Ababa. Above the entrance: 'Enter here the timeless
            fellowship of the human mind.' Inside, exceptional teachers welcomed me
            into that fellowship. They changed my life."
          </blockquote>
          <p>
            That student was lucky. He knows it.
          </p>
          <p>
            But luck shouldn't determine who enters that fellowship.
            <strong> That's why we built Lomi.</strong>
          </p>
          <div className="mission-box">
            No Ethiopian student should fail because they couldn't afford great teaching.
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="cta-content">
          <h2>Ready to start learning?</h2>
          <p>Join students across Ethiopia preparing for the Entrance Exam with Lomi</p>
          <button
            className="cta-primary large"
            onClick={() => navigate('/login')}
          >
            Start Learning Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <Logo variant="dark" height={32} />
            <span className="footer-tagline">Fresh learning. Clear understanding.</span>
          </div>
          <p className="footer-mission">
            Fresh learning for every Ethiopian student.
          </p>
          <div className="footer-links">
            <button onClick={() => navigate('/about')}>About</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>
          <div className="footer-company">
            <p>
              Built with ❤️ in Ethiopia
            </p>
            <p className="copyright">© {new Date().getFullYear()} Lomi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
